import { useMemo } from 'react';
import type { RefObject } from 'react';

import { CustomizationRenderItem } from 'features/menu/components/product-detail/product-customization/types';
import { getComboSlotItems } from 'features/menu/components/product-detail/product-customization/utils';
import { SelectorContainer } from 'features/menu/components/product-detail/styled';
import { useProductWizardContext } from 'features/menu/components/product-wizard';
import { DEFAULT_SLOT_KEY } from 'features/menu/components/product-wizard/types';
import { useProductWizardFlow } from 'features/menu/components/product-wizard/use-product-wizard-flow';
import { useStoreContext } from 'features/store';
import { ScrollView, StyleSheet, View } from 'react-native';

import { useSelectedGridRow } from 'components/list-card-group/use-selected-grid-row';
import { ProductCard } from 'components/product-card';
import {
  ComboSlotOptionsGrid,
  useScrollToCustomization,
} from 'components/product-customization/static-layout';
import { useModifierRedesignPhaseTwo } from 'experiments/use-modifier-redesign-phase-two';
import { useMenuCalories } from 'hooks/menu/use-menu-calories';
import { ClickEventComponentNames, logButtonClick } from 'state/amplitude';
import { useMainMenuContext } from 'state/menu/main-menu';
import { ILocalizedMenuEntry } from 'state/unified-menu/types';
import { useUnifiedMenuContext } from 'state/unified-menu/unified-menu-context';
import { useMenuQueryOptions } from 'state/unified-menu/use-menu-query-options';
import { getEntryPrice, stringifySanityImage } from 'state/unified-menu/utils';

import { ProductCustomizationItemModifiers } from './product-customization-item-modifiers';

interface ProductCustomizationItemProps {
  scrollViewRef: RefObject<ScrollView | null>;
}

export const ProductCustomizationItem = ({ scrollViewRef }: ProductCustomizationItemProps) => {
  const { indexedMenu } = useUnifiedMenuContext();
  const { currentFlowStep } = useProductWizardFlow();
  const { slotsSelections, selectComboSlotItem } = useProductWizardContext();
  const menuQueryOptions = useMenuQueryOptions();
  const { formatCalories } = useMenuCalories();

  const { store } = useStoreContext();
  const { activeDayParts } = useMainMenuContext();
  const storeHasBurgersForBreakfast = Boolean(store.hasBurgersForBreakfast);

  const slotKey = currentFlowStep.slotKey!;
  const selectedSlot = slotsSelections[slotKey];

  const comboSlotItems: ILocalizedMenuEntry[] = useMemo(
    () =>
      slotKey === DEFAULT_SLOT_KEY
        ? []
        : getComboSlotItems({
            menu: indexedMenu,
            slotKey,
            posServiceMode: menuQueryOptions.posServiceMode,
            activeDayParts,
            storeHasBurgersForBreakfast,
          }),
    [
      slotKey,
      indexedMenu,
      menuQueryOptions.posServiceMode,
      activeDayParts,
      storeHasBurgersForBreakfast,
    ]
  );

  const hasMultipleComboSlotOptions = comboSlotItems.length > 1;

  const { selectedRow: selectedComboSlotItemRow, handleItemPress } = useSelectedGridRow({
    items: comboSlotItems,
    selectedItemId: selectedSlot?.itemId,
  });

  const modifierCount = useMemo(() => {
    if (!selectedComboSlotItemRow) {
      return 0;
    }

    const selectedItem = comboSlotItems.find(item => item.id === selectedComboSlotItemRow.itemId);

    return selectedItem?.options?.entries?.length ?? 0;
  }, [comboSlotItems, selectedComboSlotItemRow]);

  const hasModifiers = modifierCount > 0 && hasMultipleComboSlotOptions;

  const {
    customizationRef,
    handleCustomizationLayout,
    canShowInlineCustomization,
    canMountCustomizationContent,
    onUserComboSlotSelection,
  } = useScrollToCustomization({
    scrollViewRef,
    scrollTrigger: selectedComboSlotItemRow?.itemId,
    enabled: hasModifiers,
    hasMultipleComboSlotOptions,
    modifierCount,
  });

  const showInlineCustomization = canShowInlineCustomization && hasModifiers;

  const hasInlineCustomizationForGrid = useMemo(() => {
    if (!showInlineCustomization || !selectedComboSlotItemRow?.itemId) {
      return false;
    }

    const selectedItem = comboSlotItems.find(item => item.id === selectedComboSlotItemRow.itemId);

    return !!selectedItem?.options?.entries?.length;
  }, [comboSlotItems, selectedComboSlotItemRow?.itemId, showInlineCustomization]);

  const inlineCustomizationForGrid = useMemo(() => {
    if (!hasInlineCustomizationForGrid) {
      return null;
    }

    return (
      <View
        ref={customizationRef}
        collapsable={false}
        onLayout={handleCustomizationLayout}
        style={styles.inlineCustomization}
      >
        {canMountCustomizationContent ? (
          <ProductCustomizationItemModifiers hasMultipleComboSlotOptions />
        ) : null}
      </View>
    );
  }, [
    canMountCustomizationContent,
    customizationRef,
    handleCustomizationLayout,
    hasInlineCustomizationForGrid,
  ]);

  const skipExposureLogging = !hasInlineCustomizationForGrid || !inlineCustomizationForGrid;
  useModifierRedesignPhaseTwo(skipExposureLogging);

  return (
    <>
      {comboSlotItems.length < 2 ? null : (
        <SelectorContainer>
          <ComboSlotOptionsGrid
            items={comboSlotItems}
            selectedRow={
              canShowInlineCustomization ? selectedComboSlotItemRow?.rowIndex : undefined
            }
            inlineItemCustomization={inlineCustomizationForGrid}
            renderItem={({ item, index, rowIndex }: CustomizationRenderItem) => (
              <ProductCard
                key={`item-${item.id}-${index}`}
                isSelected={item.id === selectedSlot?.itemId}
                productProps={{
                  name: item.name?.locale,
                  price: getEntryPrice(item, menuQueryOptions.posServiceMode),
                  calories: formatCalories(item.nutrition?.calories?.def),
                  image: stringifySanityImage(item.image),
                }}
                onProductPress={() => {
                  logButtonClick({
                    attributes: {
                      component: ClickEventComponentNames.PRODUCT_COMBO_SLOT_SELECTION_UPDATE,
                      name: item.name?.locale ?? '',
                      Id: item.id ?? '',
                    },
                  });

                  selectComboSlotItem(slotKey, item.id);
                  handleItemPress(item, rowIndex);
                  onUserComboSlotSelection();
                }}
              />
            )}
          />
        </SelectorContainer>
      )}

      {comboSlotItems.length <= 1 ? <ProductCustomizationItemModifiers /> : null}
    </>
  );
};

const styles = StyleSheet.create({
  inlineCustomization: {
    width: '100%',
  },
});
